import { determinePlanningSummary } from "./planningSummary";
export const calculateTotalInitiativeOwners = (initiatives = []) => {
  const ownerSet = new Set(
    initiatives
      .map(
        (initiative) =>
          initiative.initiativeOwnerInfo?.[0]
            ? `${initiative.initiativeOwnerInfo[0].firstName} ${initiative.initiativeOwnerInfo[0].lastName}`
            : null
      )
      .filter(Boolean) // Remove null or undefined entries
  );

  return parseInt(ownerSet.size);
};
export const perOwnerCount = (initiatives) => {
  const totalInitiatives = initiatives.length;
  const totalUniqueOwners = calculateTotalInitiativeOwners(initiatives);
  if (totalUniqueOwners === 0) {
    return 0; // Avoid division by zero if there are no owners
  }
  return parseInt(totalInitiatives / totalUniqueOwners);
}
export const calculateTotalNetRecurringBenefit = (initiatives) => {
  if (!initiatives || !Array.isArray(initiatives)) {
    return 0; // Ensure initiatives is a valid array
  }

  // The total accumulated benefit across all initiatives
  return initiatives.reduce((total, initiative) => {
    // Skip initiatives that don't have a valid 'impact' array
    if (!initiative.impact || !Array.isArray(initiative.impact)) {
      return total;
    }

    // Calculate the net benefit for a single initiative
    const initiativeNetBenefit = initiative.impact.reduce((acc, impact) => {
      // Only consider impacts that are recurring
      if (impact.frequencyAndDirection?.startsWith('Recurring')) {
        let amount = 0;

        // Correctly access the 'annualizedValues.plan' field
        const planValue = impact.annualizedValues?.plan;

        // Determine the actual amount based on its type
        if (typeof planValue === 'number') {
          amount = planValue; // If it's a single number
        } else if (Array.isArray(planValue)) {
          // If it's an array, sum its values
          amount = planValue.reduce((sum, val) => sum + (val || 0), 0);
        }
        // If planValue is null, undefined, or other, 'amount' remains 0

        // Add or subtract based on whether it's a 'Benefit' or 'Negative' impact
        if (impact.frequencyAndDirection.includes('Benefit')) {
          acc += amount;
        } else if (impact.frequencyAndDirection.includes('Negative')) {
          acc -= amount;
        }
      }
      return acc;
    }, 0); // Initialize accumulator for initiativeNetBenefit to 0

    return total + initiativeNetBenefit; // Add this initiative's net benefit to the total
  }, 0); // Initialize total accumulator to 0
};
export const calculateTotalNetRecurringBenefitByImpact = (impacts) => {
  // Ensure the input 'impacts' is a valid array
  if (!Array.isArray(impacts)) {
    return 0;
  }

  return impacts.reduce((totalBenefit, currentImpact) => {
    // Check if frequencyAndDirection exists and starts with 'Recurring'
    if (currentImpact?.frequencyAndDirection?.startsWith('Recurring')) {
      let amount = 0;

      // Correctly access the 'annualizedValues.plan' field
      const planValue = currentImpact.annualizedValues?.plan;

      // Safely determine the amount from planValue
      if (typeof planValue === 'number') {
        amount = planValue; // If it's a single number
      } else if (Array.isArray(planValue)) {
        // If it's an array, sum its values
        amount = planValue.reduce((sum, val) => sum + (val || 0), 0);
      }
      // If planValue is null, undefined, or other, 'amount' remains 0

      // Add or subtract based on whether it's a 'Benefit' or 'Negative' impact
      if (currentImpact.frequencyAndDirection.includes('Benefit')) {
        totalBenefit += amount;
      } else if (currentImpact.frequencyAndDirection.includes('Negative')) {
        totalBenefit -= amount;
      }
    }
    return totalBenefit;
  }, 0); // Initialize the totalBenefit accumulator to 0
};
export const calculateTotalOneTimeBenefit = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;

    const initiativeBenefit = initiative.impact.reduce((acc, impact) => {
      if (impact.frequencyAndDirection?.startsWith('One Time')) {
        let amount = 0;

        const plan = impact?.annualizedValues?.plan;

        if (typeof plan === 'number') {
          amount = plan;
        } else if (Array.isArray(plan)) {
          amount = plan.reduce((sum, val) => sum + (val || 0), 0);
        }

        if (impact.frequencyAndDirection.includes('Benefit')) {
          acc += amount;
        }
      }

      return acc;
    }, 0);

    return total + initiativeBenefit;
  }, 0);
};
export const calculateTotalOneTimeBenefitByImpact = (impact) => {
  if (!Array.isArray(impact)) {
    return 0;
  }

  return impact.reduce((totalBenefit, currentImpact) => {
    if (
      currentImpact?.frequencyAndDirection?.startsWith('One Time') &&
      currentImpact.frequencyAndDirection.includes('Benefit')
    ) {
      const plan = currentImpact?.annualizedValues?.plan;
      let amount = 0;

      if (typeof plan === 'number') {
        amount = plan;
      } else if (Array.isArray(plan)) {
        amount = plan.reduce((sum, val) => sum + (val || 0), 0);
      }

      totalBenefit += amount;
    }

    return totalBenefit;
  }, 0);
};
export const calculateTotalImplementationCost = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    let initiativeCost = 0;

    // From KPI
    if (Array.isArray(initiative.KPI)) {
      initiativeCost += initiative.KPI.reduce((acc, impact) => {
        if (
          impact.UniversalKPI &&
          impact.UniversalKPI.includes('One Time Implementation Cost for')
        ) {
          const amount = Array.isArray(impact.annuallyPlan)
            ? impact.annuallyPlan.reduce((sum, val) => sum + (val || 0), 0)
            : 0;
          return acc + amount;
        }
        return acc;
      }, 0);
    }

    // From impact
    if (Array.isArray(initiative.impact)) {
      initiativeCost += initiative.impact.reduce((acc, impact) => {
        if (impact.frequencyAndDirection === 'One implementation Cost (ETB)') {
          const amount = Array.isArray(impact.annualizedValues?.plan)
            ? impact.annualizedValues.plan.reduce((sum, val) => sum + (val || 0), 0)
            : 0;
          return acc + amount;
        }
        return acc;
      }, 0);
    }

    return total + initiativeCost;
  }, 0);
};
export const calculateTotalNetRecurringBenefitsLatest = (initiatives) => {
  if (!initiatives || !Array.isArray(initiatives)) {
    return 0;
  }
  return initiatives.reduce((total, initiative) => {
    if (!initiative.impact || !Array.isArray(initiative.impact)) {
      return total;
    }
    const initiativeNetBenefit = initiative.impact.reduce((acc, impact) => {
      if (impact.frequencyAndDirection?.startsWith('Recurring')) {
        let amount = 0;
        const planValue = impact.annualizedValues?.plan;
        if (typeof planValue === 'number') {
          amount = planValue; // If it's a single number
        } else if (Array.isArray(planValue)) {
          amount = planValue.reduce((sum, val) => sum + (val || 0), 0);
        }
        if (impact.frequencyAndDirection.includes('Benefit')) {
          acc += amount;
        } else if (impact.frequencyAndDirection.includes('Negative')) {
          acc -= amount;
        }
      }
      return acc;
    }, 0); // Initialize accumulator for initiativeNetBenefit to 0

    return total + initiativeNetBenefit; // Add this initiative's net benefit to the total
  }, 0); // Initialize total accumulator to 0
};
export const calculateStageNetRecurringBenefitsLatest = (initiatives, stage) => {
  if (!initiatives || !Array.isArray(initiatives)) {
    return 0;
  }

  return initiatives.reduce((total, initiative) => {
    if (initiative.stage !== stage) return total;

    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;

    const initiativeNetBenefit = initiative.impact.reduce((acc, impact) => {
      if (!impact.frequencyAndDirection?.startsWith('Recurring')) return acc;

      let amount = 0;
      const planValue = impact.annualizedValues?.plan;

      if (typeof planValue === 'number') {
        amount = planValue;
      } else if (Array.isArray(planValue)) {
        amount = planValue.reduce((sum, val) => sum + (val || 0), 0);
      }

      if (impact.frequencyAndDirection.includes('Benefit')) {
        acc += amount;
      } else if (impact.frequencyAndDirection.includes('Negative')) {
        acc -= amount;
      }

      return acc;
    }, 0);

    return total + initiativeNetBenefit;
  }, 0);
};
export const calculateStagesTotalNetRecurringBenefitsLatest = (initiatives, stages) => {
  if (!stages || !Array.isArray(stages)) {
    throw new Error('stages array is required');
  }
  const relevantInitiatives = initiatives.filter((initiative) =>
    stages.includes(initiative.stage)
  );

  return relevantInitiatives.reduce((total, initiative) => {
    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;

    const initiativeNetBenefit = initiative.impact.reduce((acc, impact) => {
      if (!impact.frequencyAndDirection?.startsWith('Recurring')) return acc;

      let amount = 0;
      const planValue = impact.annualizedValues?.plan;

      if (typeof planValue === 'number') {
        amount = planValue;
      } else if (Array.isArray(planValue)) {
        amount = planValue.reduce((sum, val) => sum + (val || 0), 0);
      }

      if (impact.frequencyAndDirection.includes('Benefit')) {
        acc += amount;
      } else if (impact.frequencyAndDirection.includes('Negative')) {
        acc -= amount;
      }

      return acc;
    }, 0);

    return total + initiativeNetBenefit;
  }, 0);
};
const getMonthlyAmountForImpact = (impact, stage, currentMonthIndex, initiativeTimeline) => {
  let amount = 0;

  const monthlyPlan = Array.isArray(impact.annualizedValues?.plan) ? impact.annualizedValues.plan : [];
  const monthlyActual = Array.isArray(impact.annualizedValues?.actual) ? impact.annualizedValues.actual : [];

  if (['L0', 'L1', 'L2'].includes(stage)) {
    for (let i = currentMonthIndex; i < monthlyPlan.length; i++) {
      amount += monthlyPlan[i] || 0;
    }
  } else if (['L3', 'L4'].includes(stage)) {
    for (let i = 0; i <= currentMonthIndex && i < monthlyActual.length; i++) {
      amount += monthlyActual[i] || 0;
    }
    const startIndex =
      monthlyActual.length > currentMonthIndex
        ? currentMonthIndex + 1
        : currentMonthIndex;
    for (let i = startIndex; i < monthlyPlan.length; i++) {
      amount += monthlyPlan[i] || 0;
    }
  } else if (stage === 'L5') {
    let l5MonthIndex = 0;
    const l5Entry = initiativeTimeline?.find((entry) => entry.stage === 'L5');
    const l5Date = l5Entry?.date?.actual;

    if (l5Date) {
      const date = new Date(l5Date);
      const year = date.getFullYear();
      const month = date.getMonth();
      l5MonthIndex = (year - 2023) * 12 + (month - 6);
    }

    for (let i = 0; i <= currentMonthIndex && i < monthlyActual.length; i++) {
      amount += monthlyActual[i] || 0;
    }
    const startIndex = Math.max(l5MonthIndex, currentMonthIndex + 1);
    for (let i = startIndex; i < monthlyPlan.length; i++) {
      amount += monthlyPlan[i] || 0;
    }
  }
  return amount;
};
export const calculateTotalNetRecurringBenefitsLatestByImpact = (impacts, currentMonthIndex) => {
  if (currentMonthIndex === undefined) {
    throw new Error('currentMonthIndex is required');
  }
  if (!Array.isArray(impacts)) {
    return 0;
  }

  return impacts.reduce((totalBenefit, currentImpact) => {
    if (!currentImpact?.frequencyAndDirection?.startsWith('Recurring')) {
      return totalBenefit;
    }

    const amount = getMonthlyAmountForImpact(
      currentImpact,
      currentImpact.stage,
      currentMonthIndex,
      currentImpact.timeline
    );

    if (currentImpact.frequencyAndDirection.includes('Benefit')) {
      totalBenefit += amount;
    } else if (currentImpact.frequencyAndDirection.includes('Negative')) {
      totalBenefit -= amount;
    }
    return totalBenefit;
  }, 0);
};
export const calculateTotalOneTimeBenefitsLatest = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    let initiativeCost = 0;
    if (initiative.KPI && Array.isArray(initiative.KPI)) {
      initiativeCost += initiative.KPI.reduce((acc, impact) => {
        if (impact.UniversalKPI && impact.UniversalKPI.includes('One Time Implementation Cost for')) {
          const amount = impact.annuallyPlan?.reduce((sum, val) => sum + (val || 0), 0) || 0;
          return acc + amount;
        }
        return acc;
      }, 0);
    }
    if (initiative.impact && Array.isArray(initiative.impact)) {
      initiativeCost += initiative.impact.reduce((acc, impact) => {
        if (impact.frequencyAndDirection === 'One implementation Cost (ETB)') {
          const amount = impact.annualizedValues?.plan?.reduce((sum, val) => sum + (val || 0), 0) || 0;
          return acc + amount;
        }
        return acc;
      }, 0);
    }

    return total + initiativeCost;
  }, 0);
};
export const calculateTotalOneTimeBenefitsLatestByImpact = (impacts, currentMonthIndex) => {
  if (currentMonthIndex === undefined) {
    throw new Error('currentMonthIndex is required');
  }
  if (!Array.isArray(impacts)) {
    return 0;
  }

  return impacts.reduce((totalBenefit, currentImpact) => {
    if (!currentImpact?.frequencyAndDirection?.startsWith('One Time')) return totalBenefit;

    const amount = getMonthlyAmountForImpact(
      currentImpact,
      currentImpact.stage,
      currentMonthIndex,
      currentImpact.timeline
    );

    if (currentImpact.frequencyAndDirection.includes('Benefit')) {
      totalBenefit += amount;
    }
    return totalBenefit;
  }, 0);
};
export const calculateTotalImplementationCostLatest = (initiatives, currentMonthIndex) => {
  if (currentMonthIndex === undefined) {
    throw new Error('currentMonthIndex is required');
  }
  return initiatives.reduce((total, initiative) => {
    if (!initiative.KPI || !Array.isArray(initiative.KPI)) return total;
    const stage = initiative.stage;
    const initiativeBenefit = initiative.KPI.reduce((acc, impact) => {
      let amount = 0;
      if (['L0', 'L1', 'L2'].includes(stage)) {
        if (impact.monthlyPlan && Array.isArray(impact.monthlyPlan)) {
          for (let i = currentMonthIndex; i < impact.monthlyPlan.length; i++) {
            amount += impact.monthlyPlan[i] || 0;
          }
        }
      } else if (['L3', 'L4'].includes(stage)) {
        if (impact.monthlyActual && Array.isArray(impact.monthlyActual)) {
          for (let i = 0; i <= currentMonthIndex && i < impact.monthlyActual.length; i++) {
            amount += impact.monthlyActual[i] || 0;
          }
        }
        if (impact.monthlyPlan && Array.isArray(impact.monthlyPlan)) {
          const startIndex =
            impact.monthlyActual && impact.monthlyActual.length > currentMonthIndex
              ? currentMonthIndex + 1
              : currentMonthIndex;
          for (let i = startIndex; i < impact.monthlyPlan.length; i++) {
            amount += impact.monthlyPlan[i] || 0;
          }
        }
      } else if (stage === 'L5') {
        let l5MonthIndex = 0;
        const l5Entry = initiative.timeline?.find((entry) => entry.stage === 'L5');
        const l5Date = l5Entry?.date?.actual;
        if (l5Date) {
          const date = new Date(l5Date);
          const year = date.getFullYear();
          const month = date.getMonth();
          l5MonthIndex = (year - 2023) * 12 + (month - 6);
        }
        if (impact.monthlyActual && Array.isArray(impact.monthlyActual)) {
          for (let i = 0; i <= currentMonthIndex && i < impact.monthlyActual.length; i++) {
            amount += impact.monthlyActual[i] || 0;
          }
        }
        if (impact.monthlyPlan && Array.isArray(impact.monthlyPlan)) {
          const startIndex = Math.max(l5MonthIndex, currentMonthIndex + 1);
          for (let i = startIndex; i < impact.monthlyPlan.length; i++) {
            amount += impact.monthlyPlan[i] || 0;
          }
        }
      }
      if (impact.UniversalKPI.includes('One Time Implementation Cost for')) {
        acc += amount;
      }
      return acc;
    }, 0);
    return total + initiativeBenefit;
  }, 0);
};
export const calculateTotalPlannedImpact = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;
    const initiativeImpact = initiative.impact.reduce((acc, impact) => {
      const amount = impact.annuallyPlan?.reduce((sum, val) => sum + (val || 0), 0) || 0;
      if (impact.frequencyAndDirection === 'Recurring Benefit (ETB)' || impact.frequencyAndDirection === 'One Time Benefit') {
        return acc + amount;
      }
      if (impact.frequencyAndDirection === 'One implementation Cost (ETB)' || impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
        return acc - amount;
      }
      return acc;
    }, 0);
    return total + initiativeImpact;
  }, 0);
};
export const calculatePlannedFTPImpact = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;

    const ftpImpact = initiative.impact.reduce((acc, impact) => {
      if (impact.typeOfImpact === 'FTP on Extra deposits') {
        const amount = impact.annuallyPlan?.reduce((sum, val) => sum + (val || 0), 0) || 0;

        if (impact.frequencyAndDirection === 'Recurring Benefit (ETB)') {
          return acc + amount;
        }

        if (impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
          return acc - amount;
        }
      }
      return acc;
    }, 0);

    return total + ftpImpact;
  }, 0);
};
export const calculateActualFTPImpact = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;

    const ftpImpact = initiative.impact.reduce((acc, impact) => {
      if (impact.typeOfImpact === 'FTP on Extra deposits') {
        const amount = impact.annuallyActual?.reduce((sum, val) => sum + (val || 0), 0) || 0;

        if (impact.frequencyAndDirection === 'Recurring Benefit (ETB)') {
          return acc + amount;
        }

        if (impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
          return acc - amount;
        }
      }
      return acc;
    }, 0);

    return total + ftpImpact;
  }, 0);
};
export const calculateKPIPlan = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      const amount = (kpiItem.annuallyPlan || []).reduce(
        (sum, val) => sum + (Number(val) || 0),
        0
      );

      return acc + amount;
    }, 0);

    return total + kpiSum;
  }, 0);
};
export const calculateDepositKPIPlan = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not an array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      if (kpiItem.UniversalKPI === 'Deposit') {
        const amount = (kpiItem.annuallyPlan || []).reduce(
          (sum, val) => {
            const num = Number(val);
            return isNaN(num) ? sum : sum + num;
          },
          0
        );
        return acc + amount;
      }

      return acc;
    }, 0);

    return total + kpiSum;
  }, 0);
};
export const calculateLoanKPIPlan = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not an array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      if (kpiItem.UniversalKPI === 'Loan') {
        const amount = (kpiItem.annuallyPlan || []).reduce(
          (sum, val) => {
            const num = Number(val);
            return isNaN(num) ? sum : sum + num;
          },
          0
        );
        return acc + amount;
      }

      return acc;
    }, 0);

    return total + kpiSum;
  }, 0);
};
export const calculateFinancingKPIPlan = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not an array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      if (kpiItem.UniversalKPI === 'Financing') {
        const amount = (kpiItem.annuallyPlan || []).reduce(
          (sum, val) => {
            const num = Number(val);
            return isNaN(num) ? sum : sum + num;
          },
          0
        );
        return acc + amount;
      }

      return acc;
    }, 0);

    return total + kpiSum;
  }, 0);
};
export const calculateFCYKPIPlan = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not an array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      if (kpiItem.UniversalKPI === 'FCY') {
        const amount = (kpiItem.annuallyPlan || []).reduce(
          (sum, val) => {
            const num = Number(val);
            return isNaN(num) ? sum : sum + num;
          },
          0
        );
        return acc + amount;
      }

      return acc;
    }, 0);

    return total + kpiSum;
  }, 0);
};

export const calculateKPIActual = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      const amount = (kpiItem.annuallyActual || []).reduce(
        (sum, val) => sum + (Number(val) || 0),
        0
      );

      return acc + amount;
    }, 0);

    return total + kpiSum;
  }, 0);
};
export const calculateDepositKPIActual = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not an array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      if (kpiItem.UniversalKPI === 'Deposit') {
        const amount = (kpiItem.annuallyActual || []).reduce(
          (sum, val) => {
            const num = Number(val);
            return isNaN(num) ? sum : sum + num;
          },
          0
        );
        return acc + amount;
      }

      return acc;
    }, 0);

    return total + kpiSum;
  }, 0);
};

export const calculateLoanKPIActual = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not an array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      if (kpiItem.UniversalKPI === 'Loan') {
        const amount = (kpiItem.annuallyActual || []).reduce(
          (sum, val) => {
            const num = Number(val);
            return isNaN(num) ? sum : sum + num;
          },
          0
        );
        return acc + amount;
      }

      return acc;
    }, 0);

    return total + kpiSum;
  }, 0);
};

export const calculateFinancingKPIActual = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not an array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      if (kpiItem.UniversalKPI === 'Financing') {
        const amount = (kpiItem.annuallyActual || []).reduce(
          (sum, val) => {
            const num = Number(val);
            return isNaN(num) ? sum : sum + num;
          },
          0
        );
        return acc + amount;
      }

      return acc;
    }, 0);

    return total + kpiSum;
  }, 0);
};
export const calculateAccountKPIPlan = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not an array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      if (kpiItem.UniversalKPI === 'Account Opening') {
        const amount = (kpiItem.annuallyPlan || []).reduce(
          (sum, val) => {
            const num = Number(val);
            return isNaN(num) ? sum : sum + num;
          },
          0
        );
        return acc + amount;
      }

      return acc;
    }, 0);

    return total + kpiSum;
  }, 0);
};
export const calculateAccountKPIActual = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not an array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      if (kpiItem.UniversalKPI === 'Account Opening') {
        const amount = (kpiItem.annuallyActual || []).reduce(
          (sum, val) => {
            const num = Number(val);
            return isNaN(num) ? sum : sum + num;
          },
          0
        );
        return acc + amount;
      }

      return acc;
    }, 0);

    return total + kpiSum;
  }, 0);
};
export const calculateFCYKPIActual = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    // Skip if KPI is missing or not an array
    if (!Array.isArray(initiative.KPI)) return total;

    const kpiSum = initiative.KPI.reduce((acc, kpiItem) => {
      // Skip non-financial KPI
      if (kpiItem.isFinancial === false) return acc;

      if (kpiItem.UniversalKPI === 'FCY') {
        const amount = (kpiItem.annuallyActual || []).reduce(
          (sum, val) => {
            const num = Number(val);
            return isNaN(num) ? sum : sum + num;
          },
          0
        );
        return acc + amount;
      }

      return acc;
    }, 0);

    return total + kpiSum;
  }, 0);
};
export const calculateTotalPlannedImpactUsingImpact = (impact) => {
  if (!Array.isArray(impact)) {
    return 0;
  }
  return impact.reduce((totalImpact, currentImpact) => {
    const amount = currentImpact.annuallyPlan?.reduce((sum, val) => sum + (val || 0), 0) || 0;
    if (currentImpact.frequencyAndDirection === 'Recurring Benefit (ETB)' || currentImpact.frequencyAndDirection === 'One Time Benefit') {
      return totalImpact + amount;
    }
    if (currentImpact.frequencyAndDirection === 'One implementation Cost (ETB)' || currentImpact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
      return totalImpact - amount;
    }
    return totalImpact;
  }, 0);
};
export const calculateTotalActualImpact = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;
    const initiativeImpact = initiative.impact.reduce((acc, impact) => {
      const amount = impact.annuallyActual?.reduce((sum, val) => sum + (val || 0), 0) || 0;
      if (impact.frequencyAndDirection === 'Recurring Benefit (ETB)' || impact.frequencyAndDirection === 'One Time Benefit') {
        return acc + amount;
      }
      if (impact.frequencyAndDirection === 'One implementation Cost (ETB)' || impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
        return acc - amount;
      }
      return acc;
    }, 0);
    return total + initiativeImpact;
  }, 0);
};
export const calculateTotalActualImpactUsingImpact = (impact) => {
  if (!Array.isArray(impact)) {
    return 0;
  }
  return impact.reduce((totalImpact, currentImpact) => {
    const amount = currentImpact.annuallyActual?.reduce((sum, val) => sum + (val || 0), 0) || 0;
    if (currentImpact.frequencyAndDirection === 'Recurring Benefit (ETB)' || currentImpact.frequencyAndDirection === 'One Time Benefit') {
      return totalImpact + amount;
    }
    if (currentImpact.frequencyAndDirection === 'One implementation Cost (ETB)' || currentImpact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
      return totalImpact - amount;
    }
    return totalImpact;
  }, 0);
};
export const calculateTotalImpact = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (!initiative.impact || !Array.isArray(initiative.impact)) {
      return total;
    }
    const initiativeImpact = initiative.impact.reduce((acc, impact) => {
      const amount = impact.annuallyPlan?.reduce((sum, val) => sum + (val || 0), 0) || 0;
      if (impact.frequencyAndDirection === 'Recurring Benefit (ETB)' || impact.frequencyAndDirection === 'One Time Benefit') {
        return acc + amount;
      }
      if (impact.frequencyAndDirection === 'One implementation Cost (ETB)' || impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
        return acc - amount;
      }
      return acc;
    }, 0);
    return total + initiativeImpact;
  }, 0);
};
export const calculateTotalImpactUsingImpact = (impact) => {
  if (!Array.isArray(impact)) {
    return 0;
  }
  return impact.reduce((totalImpact, currentImpact) => {
    const amount = currentImpact.annuallyPlan?.reduce((sum, val) => sum + (val || 0), 0) || 0;
    if (currentImpact.frequencyAndDirection === 'Recurring Benefit (ETB)' || currentImpact.frequencyAndDirection === 'One Time Benefit') {
      return totalImpact + amount;
    }
    if (currentImpact.frequencyAndDirection === 'One implementation Cost (ETB)' || currentImpact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
      return totalImpact - amount;
    }
    return totalImpact;
  }, 0);
};
export const calculateTotalPlannedImpactExcl = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (
      initiative.weeklyStatus === 'Cancelled' ||
      initiative.weeklyStatus === 'On Hold' ||
      initiative.weeklyStatus === 'Rejected'
    ) {
      return total; // Skip this initiative
    }
    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;
    const initiativeImpact = initiative.impact.reduce((acc, impact) => {
      const amount = impact.annuallyPlan?.reduce((sum, val) => sum + (val || 0), 0) || 0;
      return acc + amount;
    }, 0);
    return total + initiativeImpact;
  }, 0);
};
export const calculateTotalPlannedImpactExclUsingImpact = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (
      initiative.weeklyStatus === 'Cancelled' ||
      initiative.weeklyStatus === 'On Hold' ||
      initiative.weeklyStatus === 'Rejected'
    ) {
      return total; // Skip excluded initiatives
    }

    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;

    const initiativeImpact = initiative.impact.reduce((acc, impact) => {
      const amount = impact.annuallyPlan?.reduce(
        (sum, val) => sum + (val || 0),
        0
      ) || 0;

      if (
        impact.frequencyAndDirection === 'Recurring Benefit (ETB)' ||
        impact.frequencyAndDirection === 'One Time Benefit'
      ) {
        return acc + amount;
      }

      if (
        impact.frequencyAndDirection === 'One implementation Cost (ETB)' ||
        impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)'
      ) {
        return acc - amount;
      }

      return acc;
    }, 0);

    return total + initiativeImpact;
  }, 0);
};
export const calculateTotalActualImpactExcl = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (
      initiative.weeklyStatus === 'Cancelled' ||
      initiative.weeklyStatus === 'On Hold' ||
      initiative.weeklyStatus === 'Rejected'
    ) {
      return total; // Skip excluded initiatives
    }

    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;

    const initiativeImpact = initiative.impact.reduce((acc, impact) => {
      const amount = impact.annuallyActual?.reduce((sum, val) => sum + (val || 0), 0) || 0;

      if (impact.frequencyAndDirection === 'Recurring Benefit (ETB)' ||
        impact.frequencyAndDirection === 'One Time Benefit') {
        return acc + amount;
      }

      if (impact.frequencyAndDirection === 'One implementation Cost (ETB)' ||
        impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
        return acc - amount;
      }

      return acc;
    }, 0);

    return total + initiativeImpact;
  }, 0);
};
export const calculateTotalActualImpactExclUsingImpact = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (
      initiative.weeklyStatus === 'Cancelled' ||
      initiative.weeklyStatus === 'On Hold' ||
      initiative.weeklyStatus === 'Rejected'
    ) {
      return total; // Skip excluded initiatives
    }

    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;

    const initiativeImpact = initiative.impact.reduce((acc, impact) => {
      const amount = impact.annuallyActual?.reduce((sum, val) => sum + (val || 0), 0) || 0;

      if (impact.frequencyAndDirection === 'Recurring Benefit (ETB)' ||
        impact.frequencyAndDirection === 'One Time Benefit') {
        return acc + amount;
      }

      if (impact.frequencyAndDirection === 'One implementation Cost (ETB)' ||
        impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
        return acc - amount;
      }

      return acc;
    }, 0);

    return total + initiativeImpact;
  }, 0);
};
export const calculateTotalLatestEstimateImpact = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (
      initiative.weeklyStatus === 'Cancelled' ||
      initiative.weeklyStatus === 'On hold' ||
      initiative.weeklyStatus === 'Rejected'
    ) {
      return total; // Skip cancelled/on hold/rejected initiatives
    }
    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;
    const initiativeImpact = initiative.impact.reduce((acc, impact) => {
      const amount = impact.monthlyPlan?.reduce((sum, val) => sum + (val || 0), 0) || 0;
      if (impact.frequencyAndDirection === 'Recurring Benefit (ETB)' || impact.frequencyAndDirection === 'One Time Benefit') {
        return acc + amount;
      }
      if (impact.frequencyAndDirection === 'One implementation Cost (ETB)' || impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
        return acc - amount;
      }
      return acc;
    }, 0);
    return total + initiativeImpact;
  }, 0);
};
export const calculateTotalLatestEstimateImpactUsingImpact = (impact) => {
  if (!Array.isArray(impact)) {
    return 0;
  }
  return impact.reduce((totalImpact, currentImpact) => {
    const amount = currentImpact.monthlyPlan?.reduce((sum, val) => sum + (val || 0), 0) || 0;
    if (currentImpact.frequencyAndDirection === 'Recurring Benefit (ETB)' || currentImpact.frequencyAndDirection === 'One Time Benefit') {
      return totalImpact + amount;
    }
    if (currentImpact.frequencyAndDirection === 'One implementation Cost (ETB)' || currentImpact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
      return totalImpact - amount;
    }
    return totalImpact;
  }, 0);
};
export const calculateTotalLatestEstimateImpactInc = (initiatives) => {
  return initiatives.reduce((total, initiative) => {
    if (!initiative.impact || !Array.isArray(initiative.impact)) return total;
    const initiativeImpact = initiative.impact.reduce((acc, impact) => {
      const amount = impact.monthlyPlan?.reduce((sum, val) => sum + (val || 0), 0) || 0;
      if (impact.frequencyAndDirection === 'Recurring Benefit (ETB)' || impact.frequencyAndDirection === 'One Time Benefit') {
        return acc + amount;
      }
      if (impact.frequencyAndDirection === 'One implementation Cost (ETB)' || impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
        return acc - amount;
      }
      return acc;
    }, 0);
    return total + initiativeImpact;
  }, 0);
};
export const calculateTotalLatestEstimateImpactIncUsingImpact = (impact) => {
  if (!Array.isArray(impact)) {
    return 0;
  }
  return impact.reduce((totalImpact, currentImpact) => {
    const amount = currentImpact.monthlyPlan?.reduce((sum, val) => sum + (val || 0), 0) || 0;
    if (currentImpact.frequencyAndDirection === 'Recurring Benefit (ETB)' || currentImpact.frequencyAndDirection === 'One Time Benefit') {
      return totalImpact + amount;
    }
    if (currentImpact.frequencyAndDirection === 'One implementation Cost (ETB)' || currentImpact.frequencyAndDirection === 'Recurring Negative Impact (ETB)') {
      return totalImpact - amount;
    }
    return totalImpact;
  }, 0);
};
export const calculateNumberOfMilestones = (milestones) => {
  return parseInt(milestones.length);
}
// Corrected helper functions
export const countNumberOfMilestoneOwners = (milestones) => {
  if (!milestones || !Array.isArray(milestones)) {
    return 0;
  }

  let count = 0;
  milestones.forEach(milestone => {
    // Check if Owner exists and is not null/empty string.
    // Assuming Owner is a string ID.
    if (milestone.Owner && typeof milestone.Owner === 'string' && milestone.Owner.trim() !== '') {
      count++;
    }
  });

  return count;
};

export const countUniqueOwners = (milestones) => {
  if (!milestones || !Array.isArray(milestones)) {
    return 0;
  }

  const uniqueOwners = new Set();
  milestones.forEach(milestone => {
    // Check if Owner exists and is a non-empty string.
    // Assuming Owner is a string ID.
    if (milestone.Owner && typeof milestone.Owner === 'string' && milestone.Owner.trim() !== '') {
      uniqueOwners.add(milestone.Owner);
    }
  });

  return uniqueOwners.size;
};
export const calculateMilestonesPerOwnerAverage = (milestones) => {
  const totalMilestonesWithOwners = countNumberOfMilestoneOwners(milestones);
  const totalUniqueOwners = countUniqueOwners(milestones);

  if (totalUniqueOwners === 0) {
    return 0; // Avoid division by zero
  }
  return parseFloat((totalMilestonesWithOwners / totalUniqueOwners).toFixed(2));
};
export const countLateMilestones = (milestones) => {
  const currentDate = new Date();
  const msInWeek = 7 * 24 * 60 * 60 * 1000;

  return milestones.reduce((count, milestone) => {
    const startDate = milestone.startDate ? new Date(milestone.startDate) : null;
    const endDate = milestone.endDate ? new Date(milestone.endDate) : null;
    const status = milestone.status;

    if (!startDate || !endDate) return count;

    const timeDiff = currentDate.getTime() - endDate.getTime();
    const weeksLate = timeDiff / msInWeek;

    const isLate =
      status !== 'Completed' &&
      currentDate > endDate &&
      weeksLate >= 1;

    const isLateToStart =
      status === 'Not started' &&
      currentDate > startDate;

    if (isLate || isLateToStart) {
      return count + 1;
    }

    return count;
  }, 0);
};
export const calculateLateMilestoneProportion = (milestones) => {
  if (!milestones || milestones.length === 0) return 0;

  const lateCount = countLateMilestones(milestones);
  return lateCount / milestones.length;
};
export const countTotalCompletedMilestones = (milestones) => {
  if (!milestones || !Array.isArray(milestones)) {
    return 0;
  }

  let completedCount = 0;
  milestones.forEach(milestone => {
    if (milestone.status === 'Completed') {
      completedCount++;
    }
  });

  return completedCount;
};
export const calculateProportionOfCompletedMilestones = (milestones) => {
  if (!milestones || !Array.isArray(milestones) || milestones.length === 0) {
    return 0; // Avoid division by zero and handle empty array
  }

  const totalCompletedMilestones = countTotalCompletedMilestones(milestones);
  const totalMilestones = milestones.length;

  return totalCompletedMilestones / totalMilestones;
};
export const countTotalMilestonesAcrossInitiatives = (initiatives) => {
  if (!initiatives || !Array.isArray(initiatives)) {
    return 0;
  }

  let totalMilestones = 0;
  initiatives.forEach(initiative => {
    if (initiative.milestones && Array.isArray(initiative.milestones)) {
      totalMilestones += initiative.milestones.length;
    }
  });

  return totalMilestones;
};
export const calculateAverageMilestonesPerInitiative = (initiatives) => {
  if (!initiatives || !Array.isArray(initiatives) || initiatives.length === 0) {
    return 0; // Avoid division by zero and handle empty array
  }

  const totalMilestones = countTotalMilestonesAcrossInitiatives(initiatives);
  const totalInitiatives = initiatives.length;

  return totalMilestones / totalInitiatives;
};
export const calculateTotalActionItem = (actionItems) => {
  if (!actionItems || !Array.isArray(actionItems)) {
    return 0;
  }
  return actionItems.length;
};
export const calculateActionItemOwners = (actionItems) => {
  if (!actionItems || !Array.isArray(actionItems)) {
    return 0;
  }
  const uniqueOwners = new Set();
  actionItems.forEach(action => {
    // Check if owner object exists and has identifiable properties
    if (action.owner && (action.owner._id || (action.owner.firstName && action.owner.lastName))) {
      const ownerId = action.owner._id || `${action.owner.firstName} ${action.owner.lastName}`;
      uniqueOwners.add(ownerId);
    }
  });
  return uniqueOwners.size;
};
export const calculateActionItemPerOwnerAverage = (actionItems) => {
  if (!actionItems || !Array.isArray(actionItems) || actionItems.length === 0) {
    return 0;
  }
  const actionItemsWithOwners = actionItems.filter(action =>
    action.owner && (action.owner._id || (action.owner.firstName && action.owner.lastName))
  );

  const totalActionItemsWithValidOwners = actionItemsWithOwners.length;
  if (totalActionItemsWithValidOwners === 0) {
    return 0;
  }
  const numberOfUniqueOwners = calculateActionItemOwners(actionItemsWithOwners);
  if (numberOfUniqueOwners === 0) {
    return 0;
  }

  const average = totalActionItemsWithValidOwners / numberOfUniqueOwners;
  return parseFloat(average.toFixed(3));
};
export const countLateWorkstreamActions = (actionItems) => {
  if (!actionItems || !Array.isArray(actionItems)) {
    return 0;
  }
  return actionItems.filter(item => {
    const summary = determinePlanningSummary(item);
    return summary === 'Overdue' || summary === 'Cancelled'; // Including Cancelled as per previous discussion
  }).length;
};
export const proportionLateWorkstreamActions = (actionItems) => {
  const total = calculateTotalActionItem(actionItems); // Use the common total function
  if (total === 0) return 0;

  const lateCount = countLateWorkstreamActions(actionItems);
  return Number(((lateCount / total) * 100).toFixed(3));
};
export const proportionCompletedWorkstreamActions = (actionItems) => {
  const total = calculateTotalActionItem(actionItems); // Use the common total function
  if (total === 0) return 0;

  const completedCount = actionItems.filter(item => determinePlanningSummary(item) === 'Completed').length;
  return Number(((completedCount / total) * 100).toFixed(3));
};
export const calculateChangeNetRecurringBenefit = (initiatives) => {
  const filteredInitiatives = initiatives.filter(
    (initiative) => !['Cancelled', 'Rejected', 'On Hold'].includes(initiative.weeklyStatus)
  );
  const history = filteredInitiatives.flatMap((init) => init.history || []);
  const impactUpdateHistory = history.filter(
    (entry) =>
      entry.action &&
      entry.action.includes('Impact number') &&
      entry.action.includes('has been updated.')
  );
  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const oldImpactHistories = impactUpdateHistory
    .filter((entry) => new Date(entry.timestamp) < oneWeekAgo)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  let newNetRecurringBenefit = Array(5).fill(0);
  let previousNetRecurringBenefit = Array(5).fill(0);

  oldImpactHistories.forEach((entry) => {
    if (entry.changes && entry.changes.annualizedValues && entry.changes.annualizedValues.new?.plan !== undefined) {
      const newPlanValue = Number(entry.changes.annualizedValues.new.plan) || 0;
      const previousPlanValue = Number(entry.changes.annualizedValues.previous?.plan) || 0;

      if (entry.action.includes('Recurring benefits')) {
        newNetRecurringBenefit[0] += newPlanValue;
        previousNetRecurringBenefit[0] += previousPlanValue;
      } else if (entry.action.includes('Recurring Negative Impact (ETB)')) {
        newNetRecurringBenefit[0] -= newPlanValue;
        previousNetRecurringBenefit[0] -= previousPlanValue;
      }
    }
  });
  const calculateTotalNetRecurringBenefit = (initiatives) => {
    return initiatives.reduce((total, initiative) => {
      const impacts = initiative.impact || [];

      const recurringBenefit = impacts
        .filter((impact) => impact.frequencyAndDirection === 'Recurring Benefit (ETB)')
        .reduce((sum, impact) => sum + (impact.annualizedValues?.plan || 0), 0);

      const negativeRecurringImpact = impacts
        .filter((impact) => impact.frequencyAndDirection === 'Recurring Negative Impact (ETB)')
        .reduce((sum, impact) => sum + (impact.annualizedValues?.plan || 0), 0);

      return total + (recurringBenefit - negativeRecurringImpact);
    }, 0);
  };
  const totalNew = newNetRecurringBenefit.reduce((sum, value) => sum + value, 0);
  const lastWeekValue = totalNew ? totalNew : 0;
  const currentValue = calculateTotalNetRecurringBenefit(filteredInitiatives);
  return currentValue - lastWeekValue;
};
export const calculateLWRecurringBenefit = (initiatives) => {
  if (!initiatives || !Array.isArray(initiatives)) {
    console.error("Invalid input: 'initiatives' must be an array.");
    return 0;
  }
  const filteredInitiatives = initiatives.filter(
    (initiative) => !['Cancelled', 'Rejected', 'On Hold'].includes(initiative.weeklyStatus)
  );
  const now = new Date();
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const lastWeekValue = filteredInitiatives.reduce((total, initiative) => {
    const latestHistoryEntry = (initiative.history || [])
      .filter(
        (entry) =>
          new Date(entry.timestamp) < oneWeekAgo &&
          entry.action?.includes('Impact number') &&
          entry.action?.includes('has been updated.')
      )
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
    if (latestHistoryEntry && latestHistoryEntry.changes?.annualizedValues?.previous?.plan !== undefined) {
      const previousValue = Number(latestHistoryEntry.changes.annualizedValues.previous.plan) || 0;

      if (latestHistoryEntry.action.includes('Recurring Benefit (ETB)')) {
        return total + previousValue;
      } else if (latestHistoryEntry.action.includes('Recurring Negative Impact (ETB)')) {
        return total - previousValue;
      }
    }
    return total;
  }, 0);
  return lastWeekValue;
};